// frontend/src/pages/admin/AdminBookings.js
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiDownload, FiX } from 'react-icons/fi';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const statuses = ['pending','confirmed','completed','cancelled'];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const load = useCallback(() => {
    api.get('/bookings').then(r => setBookings(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status, notes) => {
    try {
      await api.put(`/bookings/${id}`, { status, adminNotes: notes });
      toast.success('Updated');
      load();
      if (selected?.id === id) setSelected({ ...selected, status, admin_notes: notes });
    } catch { toast.error('Failed'); }
  };

  const openPanel = (b) => { setSelected(b); setPanelOpen(true); };
  const closePanel = () => { setPanelOpen(false); setSelected(null); };

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  return (
    <AdminLayout title="Manage Bookings">
      <div style={{ maxWidth: 1100 }}>
        {/* Filter */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {['all', ...statuses].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              style={{ padding: '0.4rem 0.9rem', borderRadius: '50px', border: '1.5px solid', borderColor: filter === s ? 'var(--navy)' : 'var(--gray-200)', background: filter === s ? 'var(--navy)' : 'white', color: filter === s ? 'white' : 'var(--gray-500)', fontSize: '0.78rem', fontWeight: filter === s ? 600 : 400, cursor: 'pointer', textTransform: 'capitalize', fontFamily: 'Outfit,sans-serif' }}>
              {s === 'all' ? `All (${bookings.length})` : `${s} (${bookings.filter(b => b.status === s).length})`}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #F3F4F6' }}>
          <div style={{ overflowX: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--gray-400)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div>
                <p>No {filter !== 'all' ? filter : ''} bookings.</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 580 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                    {['Client', 'Service', 'Date', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.73rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b, i) => (
                    <tr key={b.id} onClick={() => openPanel(b)}
                      style={{ borderBottom: '1px solid #F9FAFB', background: selected?.id === b.id ? '#EFF6FF' : i % 2 === 0 ? 'white' : '#FAFAFA', cursor: 'pointer', transition: 'background 0.15s' }}>
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{b.client_name}</div>
                        <div style={{ color: 'var(--gray-400)', fontSize: '0.77rem' }}>{b.email}</div>
                      </td>
                      <td style={{ padding: '0.875rem 1rem', color: 'var(--gray-600)', fontSize: '0.85rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.service}</td>
                      <td style={{ padding: '0.875rem 1rem', color: 'var(--gray-500)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {b.preferred_date ? new Date(b.preferred_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '0.875rem 1rem' }}><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                      <td style={{ padding: '0.875rem 1rem', color: 'var(--gray-300)', fontSize: '0.8rem' }}>›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Mobile-friendly slide-in panel */}
        {panelOpen && selected && (
          <>
            {/* Overlay on mobile */}
            <div onClick={closePanel}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'none' }}
              id="booking-overlay" />

            <div id="booking-panel" style={{
              position: 'fixed', right: 0, top: 0, bottom: 0, width: 360,
              background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
              zIndex: 300, overflowY: 'auto', padding: '1.5rem',
              fontFamily: 'Outfit, sans-serif',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <h3 style={{ fontFamily: 'Fraunces,serif', fontSize: '1rem', color: 'var(--navy)', margin: 0 }}>Booking Details</h3>
                <button onClick={closePanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '1.2rem', padding: 0 }}><FiX /></button>
              </div>

              {[
                ['Client', selected.client_name],
                ['Email', selected.email],
                ['Phone', selected.phone || '—'],
                ['Company', selected.company || '—'],
                ['Service', selected.service],
                ['Date', selected.preferred_date ? new Date(selected.preferred_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'],
                ['Submitted', new Date(selected.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: '1px solid #F9FAFB', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--gray-400)', fontWeight: 500, flexShrink: 0 }}>{label}</span>
                  <span style={{ color: 'var(--navy)', fontWeight: 500, textAlign: 'right', marginLeft: '1rem', wordBreak: 'break-word' }}>{val}</span>
                </div>
              ))}

              {selected.message && (
                <div style={{ margin: '1rem 0', padding: '0.875rem', background: 'var(--off-white)', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--gray-600)', lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.35rem', color: 'var(--navy)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Message</div>
                  {selected.message}
                </div>
              )}

              {selected.attachments?.length > 0 && (
                <div style={{ margin: '1rem 0' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', marginBottom: '0.5rem', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Attachments</div>
                  {selected.attachments.map((a, i) => (
                    <a key={i} href={`${BACKEND}${a}`} download target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--off-white)', borderRadius: '7px', marginBottom: '0.4rem', fontSize: '0.8rem', color: 'var(--navy)', textDecoration: 'none' }}>
                      <FiDownload style={{ color: 'var(--gold)', flexShrink: 0 }} /> Attachment {i + 1}
                    </a>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '1.25rem' }}>
                <label className="form-label">Update Status</label>
                <select className="form-input" value={selected.status}
                  onChange={e => updateStatus(selected.id, e.target.value, selected.admin_notes)}
                  style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
                  {statuses.map(s => (
                    <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
                <label className="form-label">Admin Notes</label>
                <textarea className="form-input" style={{ minHeight: 80, fontSize: '0.875rem' }}
                  defaultValue={selected.admin_notes || ''}
                  placeholder="Internal notes..."
                  onBlur={e => updateStatus(selected.id, selected.status, e.target.value)} />
              </div>
            </div>

            <style>{`
              @media (max-width: 640px) {
                #booking-panel {
                  width: 100% !important;
                  top: auto !important;
                  height: 90vh !important;
                  border-radius: 20px 20px 0 0 !important;
                }
                #booking-overlay { display: block !important; }
              }
            `}</style>
          </>
        )}
      </div>
    </AdminLayout>
  );
}